import throttle from '@elab/core/utils/throttle';
/*
    Because older browsers will interpret any object in the 3rd argument as a true
    value for the capture argument, it's important to use feature detection
*/
let supportsPassive;
let passiveListenerOptions = null;

export function passiveListenerIfSupported() {
    if (supportsPassive !== undefined) return supportsPassive;
    supportsPassive = false;

    try {
        // Test via a getter in the accessor descriptor to see if the passive property is accessed
        const options = Object.defineProperty({}, 'passive', {
            get: () => (supportsPassive = { passive: true }),
        });

        window.addEventListener('testPassive', null, options);
        window.removeEventListener('testPassive', null, options);
    } catch (err) {
        window.console.error(err);
    }
    return supportsPassive;
}

export function getSwipeListeners(handleSwipe = () => {}, onPinchStart, handlePinch, onPinchEnd) {
    let swipeDir;
    let startX;
    let startY;
    let distX;
    let distY;
    const threshold = 50; // Required min distance traveled to be considered swipe
    const restraint = 100; // Maximum distance allowed at the same time in perpendicular direction
    const allowedTime = 300; // Maximum time allowed to travel that distance
    let elapsedTime;
    let startTime;
    let prevDiff;

    const getDistance = (p1, p2) =>
        Math.sqrt((p1.pageX - p2.pageX) ** 2 + (p1.pageY - p2.pageY) ** 2);

    function touchstart(e) {
        const touchobj = e.changedTouches[0];
        swipeDir = 'none';
        distX = 0;
        distY = 0;
        startX = touchobj.pageX;
        startY = touchobj.pageY;
        startTime = new Date().getTime(); // Record time when finger first makes contact with surface
    }

    touchstart.passive = true;

    const touchmove = throttle(e => {
        if (e.changedTouches.length < 2) return;
        const [touch1, touch2] = e.changedTouches;
        const currentDiff = getDistance(touch1, touch2);

        if (prevDiff) {
            handlePinch(currentDiff - prevDiff);
        } else {
            onPinchStart();
            prevDiff = currentDiff;
        }
        startX = touch1.pageX;
        startY = touch1.pageY;
    }, 15);
    touchmove.passive = true;

    return {
        touchstart,
        touchend: e => {
            const touchobj = e.changedTouches[0];
            distX = touchobj.pageX - startX; // get horizontal dist traveled by finger while in contact with surface
            distY = touchobj.pageY - startY; // get vertical dist traveled by finger while in contact with surface
            elapsedTime = new Date().getTime() - startTime; // get time elapsed

            // 1st condition for swipe met
            if (elapsedTime <= allowedTime) {
                if (Math.abs(distX) >= threshold && Math.abs(distY) <= restraint) {
                    // 2nd condition for horizontal swipe met
                    swipeDir = distX < 0 ? 'left' : 'right'; // if dist traveled is negative, it indicates left swipe
                }
            }
            onPinchEnd();
            handleSwipe(swipeDir);
            prevDiff = 0;
        },
        touchmove,
    };
}

export function getDoubleClickListener(onDoubleClick, onClick) {
    const tapDuration = 300;
    let previewTapTimer = null;

    const clickListener = () => {
        if (previewTapTimer) {
            clearTimeout(previewTapTimer);
            previewTapTimer = null;
            return onDoubleClick();
        }
        if (onClick()) return;

        previewTapTimer = setTimeout(() => {
            previewTapTimer = null;
        }, tapDuration);
    };

    return clickListener;
}

export function isTouchstartSupported() {
    return 'ontouchstart' in document.documentElement;
}

export function windowResizeEvent() {
    window.dispatchEvent(new Event('resize'));
}
